import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  EVENTS,
  SCHEDULE_EVENT_TYPES,
  SCHEDULE_ANALYTICS_TAB,
  ANALYTICS_SOURCES,
} from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import analyticsService from 'src/helpers/analytics.service';
import { ScreenTitle, Tabs, Button } from 'src/components/design-system';
import {
  EducationalEventsProvider,
  useEducationalEventsContext,
} from './educational-events-context';
import { EducationalEventsLayout } from './educational-events-layout';
import { SOURCE_PARAM, SOURCE_REPORT } from './helpers/event-view.helpers';
import { EventFormModal } from './components/event-form/event-form-modal';
import { ReportsTab } from './components/reports/reports-tab';
import styles from './educational-events.module.scss';

// The URL tab param and the analytics `tab` dimension share one vocabulary.
const TAB_KEYS = { EVENTS: SCHEDULE_ANALYTICS_TAB.EVENTS, REPORTS: SCHEDULE_ANALYTICS_TAB.REPORTS };
const TAB_PARAM = 'tab';

function EducationalEventsPageContent() {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const schedulesLang = useSelector(getLang('SCHEDULES'));
  const { openCreateForm, canManageEvents } = useEducationalEventsContext();

  const [searchParams, setSearchParams] = useSearchParams();
  // Reports are collaborator-only; a pasted `?tab=reports` from a viewer
  // falls back to the Events tab.
  const requestedTab = searchParams.get(TAB_PARAM);
  const activeTab =
    requestedTab === TAB_KEYS.REPORTS && canManageEvents ? TAB_KEYS.REPORTS : TAB_KEYS.EVENTS;

  // Set by the report tables' event links — the report shares this pathname, so
  // the URL is the only signal that survives the navigation tracker's reset.
  // Consumed on the first Events-tab view: `onTabChange` copies every existing
  // param, so a later tab switch would otherwise be re-credited to the report.
  const fromReportRef = useRef(searchParams.get(SOURCE_PARAM) === SOURCE_REPORT);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.EVENTS) return;
    const fromReport = fromReportRef.current;
    fromReportRef.current = false;
    analyticsService.track(EVENTS.SCHEDULE.PAGE_VIEW, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      tab: TAB_KEYS.EVENTS,
      ...(fromReport && { source: ANALYTICS_SOURCES.EDUCATIONAL_EVENTS_REPORT }),
    });
  }, [activeTab]);

  const onTabChange = useCallback(
    key => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (key === TAB_KEYS.EVENTS) next.delete(TAB_PARAM);
          else next.set(TAB_PARAM, key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const tabItems = [
    {
      key: TAB_KEYS.EVENTS,
      label: lang.TAB_EVENTS,
      children: <EducationalEventsLayout />,
    },
    ...(canManageEvents
      ? [{ key: TAB_KEYS.REPORTS, label: lang.TAB_REPORTS, children: <ReportsTab /> }]
      : []),
  ];

  return (
    <div className={classNames('new-design-system', styles.root)}>
      <div className={styles.header}>
        <ScreenTitle
          title={schedulesLang.NAV_EDUCATIONAL_EVENTS}
          containerStyle={styles.screenTitle}
        />
        {canManageEvents && (
          <Button
            type="primary"
            ghost
            size="large"
            onClick={() => {
              analyticsService.track(EVENTS.SCHEDULE.ADD_EVENT_CLICK, {
                type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
              });
              openCreateForm();
            }}>
            {schedulesLang.ADD_EDUCATIONAL_EVENT}
          </Button>
        )}
      </div>
      <Tabs activeKey={activeTab} onChange={onTabChange} items={tabItems} />
      <EventFormModal />
    </div>
  );
}

export function EducationalEventsPage() {
  return (
    <EducationalEventsProvider>
      <EducationalEventsPageContent />
    </EducationalEventsProvider>
  );
}
